import { useState, useCallback } from 'react';
import { CursoFormState, CURSO_FORM_INITIAL, CursoPayload } from '../types/curso.types';
import { cursoService } from '../services/cursoService';

// ============================================================
// useCursoForm – creación y edición de cursos
// ============================================================

export interface CursoFormErrors { [field: string]: string }

export const useCursoForm = (idCurso: number | null, onSuccess: (cursoId?: number, nombre?: string) => void) => {
  const isEditing = idCurso !== null;

  const [form, setForm]               = useState<CursoFormState>(CURSO_FORM_INITIAL);
  const [errors, setErrors]           = useState<CursoFormErrors>({});
  const [submitting, setSubmitting]   = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [loadingInit, setLoadingInit] = useState(false);

  // ── Cargar datos en modo edición ──────────────────────────
  const loadCurso = useCallback(async () => {
    if (!isEditing || idCurso === null) return;
    setLoadingInit(true);
    try {
      const data = await cursoService.obtener(idCurso);
      setForm({ nombre: data.nombre, descripcion: data.descripcion, activo: data.activo });
    } catch { /* mantiene vacío */ }
    finally { setLoadingInit(false); }
  }, [idCurso, isEditing]);

  // ── Cambio de campo ───────────────────────────────────────
  const handleChange = useCallback((
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setErrors(prev => { const n = { ...prev }; delete n[name]; return n; });
    setForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  }, []);

  // ── Validación ────────────────────────────────────────────
  const validate = (): CursoFormErrors => {
    const e: CursoFormErrors = {};
    if (!form.nombre.trim())      e.nombre      = 'El nombre es requerido';
    if (!form.descripcion.trim()) e.descripcion = 'La descripción es requerida';
    return e;
  };

  // ── Submit ────────────────────────────────────────────────
  const handleSubmit = useCallback(async () => {
    const e = validate();
    if (Object.keys(e).length > 0) { setErrors(e); return; }

    setSubmitting(true);
    setSubmitError(null);
    try {
      const payload: CursoPayload = {
        nombre:      form.nombre.trim(),
        descripcion: form.descripcion.trim(),
        activo:      form.activo,
      };
      if (isEditing && idCurso) {
        await cursoService.modificar(idCurso, payload);
        onSuccess();
      } else {
        const nuevo = await cursoService.crear(payload);
        onSuccess(nuevo.idCurso, nuevo.nombre);
      }
    } catch (err: unknown) {
      setSubmitError(err instanceof Error ? err.message : 'Error al guardar el curso');
    } finally {
      setSubmitting(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, isEditing, idCurso]);

  return {
    form, errors, submitting, submitError, loadingInit,
    handleChange, handleSubmit, loadCurso,
  };
};
